/**
 * THE date-range filter, shared by the sheet and Insights - one component, so
 * the two pages can never drift apart in wording or behaviour. (They used to
 * carry near-identical private copies; this is the merge those copies promised.)
 *
 * What is deliberately NOT here is the maths: both pages call the same
 * `assessmentsInRange` / `defaultRange`, so they cannot disagree about which
 * columns a range contains.
 *
 * `hiddenCount` earns its place: when a filter empties part of a page, the
 * teacher has to be told it was the filter and not lost work.
 */

import { cn } from "@/lib/utils";
import { DatePicker } from "@/gradebook/DatePicker";

export function DateRangeFilter({ range, onChange, fullRange, hiddenCount, className }) {
  const narrowed = range.from !== fullRange.from || range.to !== fullRange.to;
  /* Picker-only bounds - see DatePicker for why these are not native date
   * inputs (the blue popup, and keyboard edits that passed through year 45). */
  const field =
    "h-7 rounded-md border border-border bg-card px-2 text-[12px] text-foreground " +
    "focus:outline-none focus:ring-1 focus:ring-primary";

  /* Grey fill, primary outline - the container marks itself as the sheet's
   * one filter without shouting in solid green next to the page's buttons. */
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 rounded-lg border border-primary bg-muted px-3 py-1.5",
        className,
      )}
    >
      <span className="text-[12px] font-medium text-foreground">Show from</span>
      <DatePicker
        value={range.from}
        max={range.to || undefined}
        ariaLabel="Show assessments from"
        onChange={(v) => onChange({ ...range, from: v })}
        buttonClassName={field}
      />
      <span className="text-[12px] font-medium text-foreground">to</span>
      <DatePicker
        value={range.to}
        min={range.from || undefined}
        ariaLabel="Show assessments until"
        onChange={(v) => onChange({ ...range, to: v })}
        buttonClassName={field}
      />
      {narrowed && (
        <button
          type="button"
          onClick={() => onChange(fullRange)}
          title="Show every assessment"
          className="h-7 px-1.5 text-[12px] font-medium text-primary underline-offset-2 transition-colors hover:underline"
        >
          Reset
        </button>
      )}
      {hiddenCount > 0 && (
        <span className="text-[12px] tabular-nums text-muted-foreground">
          {hiddenCount} hidden
        </span>
      )}
    </div>
  );
}
